// COFRE — senhas, tokens e senha do certificado digital (2026-08-20).
//
// A tela tem uma regra de conduta antes de ter funcionalidade: **o valor nunca
// fica na tela**. Não há coluna de senha, não há "mostrar sempre", não há cache
// no localStorage. Revelar é um ato deliberado, some sozinho em 30 segundos, e
// quem revelou fica registrado — a pessoa vê esse aviso na hora, porque ninguém
// deve descobrir depois que foi auditado.
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  App as AntApp, Alert, Button, Empty, Flex, Form, Input,
  Modal, Popconfirm, Select, Space, Table, Tag, Tooltip, Typography, DatePicker,
} from 'antd';
import dayjs from 'dayjs';
import {
  listarSegredos, revelarSegredo, salvarSegredo, excluirSegredo, listarAcessosCofre,
  Segredo, AcessoCofre, TIPO_SEGREDO, TipoSegredo,
} from './apiCofre';
import { listarDocumentos } from './api';
import type { Documento } from './types';
import { fmtDataBr } from '../../utils/dataFmt';

const { Text, Paragraph } = Typography;

// Quanto tempo o valor fica visível depois de revelado. Curto de propósito:
// tempo suficiente para copiar, curto demais para virar aba esquecida aberta.
const SEGUNDOS_VISIVEL = 30;

export function PainelCofre() {
  const { message } = AntApp.useApp();
  const [carregando, setCarregando] = useState(true);
  const [segredos, setSegredos] = useState<Segredo[]>([]);
  const [podeRevelar, setPodeRevelar] = useState(false);
  const [podeGerenciar, setPodeGerenciar] = useState(false);
  const [busca, setBusca] = useState('');

  // O valor revelado vive SÓ aqui, em memória, e por pouco tempo.
  const [revelado, setRevelado] = useState<{ id: number; rotulo: string; valor: string } | null>(null);
  const [restam, setRestam] = useState(0);
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);

  const [edicao, setEdicao] = useState<Partial<Segredo> | null>(null);
  const [form] = Form.useForm();
  const [salvando, setSalvando] = useState(false);
  const [acessos, setAcessos] = useState<AcessoCofre[] | null>(null);
  // Documentos do acervo, para o campo de vínculo. Carregados só quando o
  // cadastro abre — 70 linhas não valem uma consulta a cada montagem da tela.
  const [docs, setDocs] = useState<Documento[]>([]);
  const [docsCarregando, setDocsCarregando] = useState(false);

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const r = await listarSegredos(busca ? { busca } : {});
      setSegredos(r.segredos || []);
      setPodeRevelar(!!r.pode_revelar);
      setPodeGerenciar(!!r.pode_gerenciar);
    } catch (e: any) {
      message.error('Falha ao carregar o cofre: ' + (e.response?.data?.error || e.message));
    } finally { setCarregando(false); }
  }, [busca, message]);

  useEffect(() => { carregar(); }, [carregar]);

  // Limpa o valor da memória quando o componente sai — trocar de aba não pode
  // deixar a senha pendurada num state vivo.
  useEffect(() => () => { if (timer.current) clearInterval(timer.current); }, []);

  function esconder() {
    if (timer.current) { clearInterval(timer.current); timer.current = null; }
    setRevelado(null);
    setRestam(0);
  }

  async function revelar(s: Segredo) {
    try {
      const r = await revelarSegredo(s.id);
      esconder();
      setRevelado({ id: s.id, rotulo: r.rotulo, valor: r.valor });
      setRestam(SEGUNDOS_VISIVEL);
      timer.current = setInterval(() => {
        setRestam((n) => {
          if (n <= 1) { esconder(); return 0; }
          return n - 1;
        });
      }, 1000);
    } catch (e: any) {
      const st = e?.response?.status;
      message.error(st === 404
        ? 'Segredo não encontrado.'
        : 'Não foi possível revelar: ' + (e.response?.data?.error || e.message));
    }
  }

  async function copiar(valor: string) {
    try {
      await navigator.clipboard.writeText(valor);
      message.success('Copiado. A área de transferência não se limpa sozinha — cole e siga.');
    } catch {
      message.warning('O navegador bloqueou a cópia. Selecione o texto à mão.');
    }
  }

  async function carregarDocs() {
    if (docs.length || docsCarregando) return;
    setDocsCarregando(true);
    try {
      // O backend já esconde documento de categoria restrita de quem não pode
      // vê-lo, então esta lista é segura para qualquer um que abra o cadastro.
      const r = await listarDocumentos({});
      setDocs(r.documentos || []);
    } catch {
      // Sem a lista o campo fica vazio, e o resto do cadastro continua servindo.
    } finally { setDocsCarregando(false); }
  }

  function abrirEdicao(s?: Segredo) {
    carregarDocs();
    setEdicao(s || {});
    // 🚨 `resetFields` antes do `setFieldsValue`: o segundo faz MERGE e não
    //    apaga o que já estava no formulário, e o Modal não tem
    //    `destroyOnClose`. Sem isto, abrir "Editar" no certificado, cancelar e
    //    clicar em "Novo segredo" trazia titular, validade, URL e id do
    //    documento do anterior — nascia um PIN de cartão "vencendo" em
    //    10/05/2027, e o e-mail de segunda passava a cobrar a renovação de
    //    algo que não vence.
    form.resetFields();
    form.setFieldsValue(s
      ? { ...s, valor: undefined, validade: s.validade ? dayjs(s.validade) : null }
      : { tipo: 'senha_portal', restrito: false });
  }

  async function salvar() {
    let v: any;
    try { v = await form.validateFields(); } catch { return; }
    setSalvando(true);
    try {
      const r = await salvarSegredo({
        ...v,
        id: edicao?.id,
        validade: v.validade ? dayjs(v.validade).format('YYYY-MM-DD') : null,
      });
      message.success(r.criado ? 'Segredo guardado.'
        : r.valor_trocado ? 'Segredo atualizado (valor trocado).' : 'Dados atualizados.');
      setEdicao(null);
      carregar();
    } catch (e: any) {
      message.error('Falha ao salvar: ' + (e.response?.data?.error || e.message));
    } finally { setSalvando(false); }
  }

  async function excluir(s: Segredo) {
    try {
      const r = await excluirSegredo(s.id);
      const dias = r?.recuperavel_por_dias;
      message.success(dias
        ? `Segredo excluído. Ainda dá para recuperar por ${dias} dias — depois disso some de vez.`
        : 'Segredo excluído. O registro de quem o acessou continua no histórico.');
      carregar();
    } catch (e: any) {
      message.error('Falha ao excluir: ' + (e.response?.data?.error || e.message));
    }
  }

  async function verAcessos() {
    try {
      const r = await listarAcessosCofre({ limite: 200 });
      setAcessos(r.acessos || []);
    } catch (e: any) {
      message.error('Falha ao ler o histórico: ' + (e.response?.data?.error || e.message));
    }
  }

  const colunas = [
    {
      title: 'Segredo', dataIndex: 'rotulo', key: 'rotulo', width: '30%',
      render: (_: any, s: Segredo) => (
        <Space direction="vertical" size={0}>
          <Space size={6}>
            <Text strong>{s.rotulo}</Text>
            {s.restrito && <Tag color="red">restrito</Tag>}
          </Space>
          {s.usuario_login && <Text type="secondary" style={{ fontSize: 11 }}>login: {s.usuario_login}</Text>}
          {s.documento_codigo && (
            <Text type="secondary" style={{ fontSize: 11 }}>
              arquivo: {s.documento_codigo} — {s.documento_titulo}
            </Text>
          )}
        </Space>
      ),
    },
    {
      title: 'Tipo', dataIndex: 'tipo', key: 'tipo', width: 170,
      render: (t: TipoSegredo) => <Tag color={TIPO_SEGREDO[t]?.cor}>{TIPO_SEGREDO[t]?.label || t}</Tag>,
    },
    { title: 'Titular', dataIndex: 'titular', key: 'titular', width: '18%',
      render: (v: string | null) => v || <Text type="secondary">—</Text> },
    {
      title: 'Validade', dataIndex: 'validade', key: 'validade', width: 130,
      render: (v: string | null) => {
        if (!v) return <Text type="secondary">—</Text>;
        const dias = dayjs(v).diff(dayjs(), 'day');
        const cor = dias < 0 ? 'red' : dias <= 30 ? 'orange' : undefined;
        return <Tag color={cor}>{fmtDataBr(v)}</Tag>;
      },
    },
    {
      title: 'Último acesso', dataIndex: 'revelado_em', key: 'revelado_em', width: 140,
      // Sem `fromNow()`: o plugin relativeTime do dayjs não está registrado no
      // projeto, e chamar sem ele devolve undefined em silêncio.
      render: (v: string | null) => v
        ? <Tooltip title={dayjs(v).format('DD/MM/YYYY HH:mm')}>
            <Text style={{ fontSize: 12 }}>{dayjs(v).format('DD/MM/YY HH:mm')}</Text>
          </Tooltip>
        : <Text type="secondary">nunca</Text>,
    },
    {
      title: '', key: 'acoes', width: 200,
      render: (_: any, s: Segredo) => (
        <Space size={4}>
          {podeRevelar && (
            <Button size="small" type="primary" ghost onClick={() => revelar(s)}>Revelar</Button>
          )}
          {podeGerenciar && <Button size="small" onClick={() => abrirEdicao(s)}>Editar</Button>}
          {podeGerenciar && (
            <Popconfirm title="Excluir este segredo?"
              description="A AWS ainda guarda por 7 dias, então dá para voltar atrás. O histórico de quem o acessou permanece."
              okText="Excluir" okButtonProps={{ danger: true }} cancelText="Cancelar"
              onConfirm={() => excluir(s)}>
              <Button size="small" danger>Excluir</Button>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Alert
        type="info" showIcon style={{ marginBottom: 12 }}
        message="Toda revelação fica registrada"
        description={
          <span style={{ fontSize: 12 }}>
            O valor fica guardado fora do sistema, no cofre da AWS, e só é lido no momento em
            que alguém clica em "Revelar" — ficando registrado quem e quando.
            Some da tela sozinho em {SEGUNDOS_VISIVEL} segundos.
            O arquivo do certificado continua no acervo; aqui fica só a senha dele.
          </span>
        }
      />

      {revelado && (
        <Alert
          type="warning" showIcon={false} style={{ marginBottom: 12 }}
          message={
            <Flex justify="space-between" align="center" wrap gap={8}>
              <Space direction="vertical" size={2}>
                <Text type="secondary" style={{ fontSize: 11 }}>{revelado.rotulo}</Text>
                <Text code style={{ fontSize: 16, userSelect: 'all' }}>{revelado.valor}</Text>
              </Space>
              <Space>
                <Text type="secondary" style={{ fontSize: 12 }}>some em {restam}s</Text>
                <Button size="small" onClick={() => copiar(revelado.valor)}>Copiar</Button>
                <Button size="small" onClick={esconder}>Esconder agora</Button>
              </Space>
            </Flex>
          }
        />
      )}

      <Flex justify="space-between" align="center" wrap gap={8} style={{ marginBottom: 10 }}>
        <Input.Search
          placeholder="Buscar por rótulo, titular ou login"
          allowClear style={{ maxWidth: 380 }}
          onSearch={(v) => setBusca(v)} onChange={(e) => { if (!e.target.value) setBusca(''); }}
        />
        <Space>
          {podeGerenciar && <Button onClick={verAcessos}>Quem acessou</Button>}
          {podeGerenciar && <Button type="primary" onClick={() => abrirEdicao()}>Novo segredo</Button>}
        </Space>
      </Flex>

      <Table
        rowKey="id" size="small" loading={carregando}
        dataSource={segredos} columns={colunas as any}
        tableLayout="fixed" pagination={false}
        locale={{ emptyText: <Empty description="Nenhum segredo guardado ainda" /> }}
      />

      {/* ── Cadastro ── */}
      <Modal
        open={!!edicao} onCancel={() => setEdicao(null)} onOk={salvar}
        confirmLoading={salvando} okText="Guardar" cancelText="Cancelar"
        title={edicao?.id ? `Editar — ${edicao.rotulo}` : 'Novo segredo'}
        width="94vw" style={{ maxWidth: 720, top: 16 }}
        styles={{ body: { maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' } }}
      >
        <Form form={form} layout="vertical" size="small">
          <Form.Item name="rotulo" label="Rótulo"
            rules={[{ required: true, min: 3, message: 'Mínimo 3 caracteres' }]}>
            <Input placeholder="Portal eCAC — e-CNPJ Hydrodata" />
          </Form.Item>
          <Flex gap={12} wrap>
            <Form.Item name="tipo" label="Tipo" rules={[{ required: true }]} style={{ flex: '1 1 200px' }}>
              <Select options={Object.entries(TIPO_SEGREDO).map(([v, c]) => ({ value: v, label: c.label }))} />
            </Form.Item>
            <Form.Item name="titular" label="Titular" style={{ flex: '1 1 200px' }}>
              <Input placeholder="Hydrodata Engenharia / nome do sócio" />
            </Form.Item>
          </Flex>
          <Form.Item name="valor"
            label={edicao?.id ? 'Novo valor (deixe em branco para não trocar)' : 'Valor'}
            rules={edicao?.id ? [] : [{ required: true, message: 'Informe o valor' }]}>
            <Input.Password placeholder="a senha, o token ou o PIN" autoComplete="new-password" />
          </Form.Item>
          <Flex gap={12} wrap>
            <Form.Item name="usuario_login" label="Login (pode ficar visível)" style={{ flex: '1 1 220px' }}>
              <Input placeholder="29.388.072/0001-30" />
            </Form.Item>
            <Form.Item name="validade" label="Vence em" style={{ flex: '0 0 160px' }}>
              <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} />
            </Form.Item>
          </Flex>
          <Form.Item name="url_portal" label="Endereço do portal">
            <Input placeholder="https://cav.receita.fazenda.gov.br" />
          </Form.Item>
          <Form.Item name="documento_id" label="Arquivo no acervo a que esta senha se refere"
            tooltip="O certificado (.pfx/.p12) fica no acervo de Documentos da empresa; aqui guarda-se só a senha dele. Digite parte do nome para procurar.">
            <Select
              showSearch allowClear loading={docsCarregando}
              placeholder="Procure pelo nome ou pelo código do documento"
              // `optionFilterProp="label"` porque o label é o texto que a
              // pessoa lê e digita; sem isto o antd filtra pelo `value`, que
              // aqui é o id numérico — exatamente o que ela não sabe.
              optionFilterProp="label"
              notFoundContent={docsCarregando ? 'carregando…' : 'nenhum documento encontrado'}
              options={docs.map((d) => ({
                value: d.id,
                label: `${d.codigo || 's/ código'} — ${d.titulo}`,
              }))}
            />
          </Form.Item>
          <Form.Item name="observacao" label="Observação (dica de contexto — nunca a senha)">
            <Input.TextArea rows={2} />
          </Form.Item>
          <Form.Item name="restrito" label="Restrito (dado de sócio / pessoal)"
            tooltip="Some da lista de quem não tem permissão de documentos de sócios.">
            <Select options={[{ value: false, label: 'Não' }, { value: true, label: 'Sim' }]} />
          </Form.Item>
        </Form>
      </Modal>

      {/* ── Histórico ── */}
      <Modal
        open={acessos !== null} onCancel={() => setAcessos(null)} footer={null}
        title="Quem acessou o cofre"
        width="94vw" style={{ maxWidth: 900, top: 16 }}
        styles={{ body: { maxHeight: 'calc(100vh - 180px)', overflowY: 'auto' } }}
      >
        <Paragraph type="secondary" style={{ fontSize: 12 }}>
          Registro que não se apaga: excluir um segredo mantém aqui o rótulo que ele tinha.
        </Paragraph>
        <Table
          rowKey="id" size="small" pagination={{ pageSize: 25 }}
          dataSource={acessos || []}
          columns={[
            { title: 'Quando', dataIndex: 'em', width: 150,
              render: (v: string) => dayjs(v).format('DD/MM/YYYY HH:mm') },
            { title: 'Quem', dataIndex: 'usuario_email', width: '28%' },
            { title: 'O quê', dataIndex: 'acao', width: 130,
              render: (a: string) => <Tag color={a === 'revelou' ? 'orange' : a === 'excluiu' ? 'red' : 'default'}>{a}</Tag> },
            { title: 'Segredo', dataIndex: 'rotulo_snap',
              render: (v: string, r: AcessoCofre) => (
                <Space size={6}>
                  <Text>{v}</Text>
                  {r.segredo_id === null && <Tag>excluído</Tag>}
                </Space>
              ) },
            { title: 'Motivo', dataIndex: 'detalhe',
              render: (v: string | null) => v || <Text type="secondary">—</Text> },
          ] as any}
        />
      </Modal>
    </div>
  );
}
